// Column definitions and configuration for DataGrid components
// This file centralizes all column-related configuration to make it easier to maintain
import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { columnHeaderMappings, FIELD_TYPES } from '../../../common/mappings.js';
import { formatCourtDateDisplayDay } from '../../../common/dateUtils.js';

/** Preferred column order for Data Analysis grid (extras not in API response are ignored). */
export const DATA_ANALYSIS_COLUMN_ORDER = [
  'parcel_id',
  'Address',
  'Style',
  'Sqft',
  'YearBuilt',
  'Bath',
  'HalfBath',
  'Acres',
  'PhotoURL',
  'Latitude',
  'Longitude',
  'Town',
  'ZipCode',
  'InGroundPool',
  'TotalGarageCap',
  'FinishedBasement',
  'TransactionID',
  'SalePrice',
  'SaleDate',
  'MLSNumber',
  'IFMV',
  'IFMVRatio',
  'ALFlag',
  'Taxes',
  'PropertyAssessment',
  'MarketValue',
  'AssessedValue',
  'PCC',
  'ConditionIn',
  'Grade',
  'Fireplaces',
  'Deck',
  'Porch',
  'Patio',
];

/** Visible-by-default subset; optional columns toggled via the column visibility panel. */
export const DATA_ANALYSIS_DEFAULT_VISIBLE_FIELDS = new Set([
  'parcel_id',
  'Address',
  'Style',
  'Sqft',
  'YearBuilt',
  'Bath',
  'HalfBath',
  'Acres',
  'PhotoURL',
  'Latitude',
  'Longitude',
  'Town',
  'ZipCode',
  'InGroundPool',
  'TotalGarageCap',
  'FinishedBasement',
  'SalePrice',
  'SaleDate',
  'MLSNumber',
  'IFMV',
  'IFMVRatio',
  'ALFlag',
  'Taxes',
  'PropertyAssessment',
  'MarketValue',
  'AssessedValue',
  'PCC',
]);

// Utility function to reorder columns based on preferred order
export const reorderColumns = (columns, preferredOrder) => {
  return columns.sort((a, b) => {
    const aIndex = preferredOrder.indexOf(a.field);
    const bIndex = preferredOrder.indexOf(b.field);
    
    // If both columns are in preferred order, sort by their order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    // If only a is in preferred order, it comes first
    if (aIndex !== -1) return -1;
    // If only b is in preferred order, it comes first
    if (bIndex !== -1) return 1;
    // If neither is in preferred order, maintain original order
    return 0;
  });
};

// Build columns from records with proper formatting and styling
export const buildColumnsFromRecords = (records) => {
  if (!records || records.length === 0) return [];

  const fieldNames = [...new Set(records.flatMap((row) => Object.keys(row)))];

  let columns = fieldNames.map(key => {
    const column = {
      field: key,
      headerName: columnHeaderMappings[key] || key,
      flex: 1,
      minWidth: 130,
    };

    // Add field type based on FIELD_TYPES mapping
    const fieldType = FIELD_TYPES[key];
    if (fieldType) {
      column.type = fieldType;
      
      // Add specific formatting for different field types
      if (fieldType === 'percent') {
        column.valueFormatter = (value) => {
          if (value == null || value === '' || value === undefined) return '';
          
          const numValue = typeof value === 'number' ? value : parseFloat(value);
          if (isNaN(numValue)) return '';
          
          return `${(numValue * 100).toFixed(2)}%`;
        };
      } else if (key === 'IFMVRatio') {
        // Special formatting for IFMVRatio as percentage (even though it's numeric type)
        column.valueFormatter = (value) => {
          if (value == null || value === '' || value === undefined) return '';
          
          const numValue = typeof value === 'number' ? value : parseFloat(value);
          if (isNaN(numValue)) return '';
          
          return `${(numValue * 100).toFixed(2)}%`;
        };
        column.align = 'right';
        column.headerAlign = 'right';
      } else if (fieldType === 'currency') {
        column.valueFormatter = (value) => {
          if (value == null || value === '' || value === undefined) return '';
          
          const numValue = typeof value === 'number' ? value : parseFloat(value);
          if (isNaN(numValue)) return '';
          
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(numValue);
        };
      } else if (fieldType === 'date') {
        column.valueFormatter = (value) => formatCourtDateDisplayDay(value);
      } else if (fieldType === 'boolean') {
        column.valueFormatter = (value) => {
          if (value === 1 || value === '1' || value === true) return 'Yes';
          if (value === 0 || value === '0' || value === false) return 'No';
          return '';
        };
      } else if (fieldType === 'number' && key === 'ALFlag') {
        column.valueFormatter = (value) => {
          if (value === 1 || value === '1' || value === true) return 'Yes';
          if (value === 0 || value === '0') return 'No';
          return '';
        };
      } else if (key === 'MLSNumber') {
        column.valueFormatter = (value) => {
          if (value == null || value === '' || value === undefined || value === 'None') return '';
          return value;
        };
      }
    }

    // Skip Description column entirely
    if (key === 'Description') {
      return null;
    }

    column.cellClassName = (params) => {
      const description = params.row.Description || '';
      if (!description || !key) return '';
      
      // Use regex to match exact field patterns to avoid substring issues
      // (e.g., Bath_NA should not match HalfBath_NA)
      const regexNA = new RegExp(`\\(${key}_NA\\)`, 'g');
      const regexEXTREME = new RegExp(`\\(${key}_EXTREME\\)`, 'g');
      return regexNA.test(description) || regexEXTREME.test(description) ? 'missing-data-cell' : '';
    };

    column.renderCell = (params) => {
      const description = params.row.Description || '';
      let isMissing = false;
      if (description && key) {
        // Use regex to match exact field patterns to avoid substring issues
        const regexNA = new RegExp(`\\(${key}_NA\\)`, 'g');
        const regexEXTREME = new RegExp(`\\(${key}_EXTREME\\)`, 'g');
        isMissing = regexNA.test(description) || regexEXTREME.test(description);
      }

      const content = (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start'
        }}>
          {key === 'InGroundPool'
            ? (params.value === 1 || params.value === '1' ? 'Yes' : 'No')
            : params.value}
        </div>
      );

      return isMissing ? (
        <Tooltip
          title={`Missing ${columnHeaderMappings[key] || key}`}
          placement="bottom"
          disableInteractive
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                marginTop: '4px !important'
              }
            }
          }}
        >
          {content}
        </Tooltip>
      ) : content;
    };

    return column;
  }).filter(Boolean); // Remove null columns (Description)

  return reorderColumns(columns, DATA_ANALYSIS_COLUMN_ORDER);
};
